/**
 * Collection Grid - Filters & Pagination
 * Uses native URL navigation for reliable filtering and pagination
 */

class CollectionGrid {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.filterForm = container.querySelector('[data-filter-form]');
    this.productsGrid = container.querySelector('[data-products-grid]');
    this.toast = document.querySelector('[data-cart-toast]');

    this.init();
  }

  init() {
    this.bindFilterEvents();
    this.bindPaginationEvents();
    this.bindAddToCartEvents();
    this.initDropdowns();
    this.initCategorySlider();
    this.updateActiveFiltersDisplay();
    this.initSeoToggle();
  }

  // =========================================================================
  // Dropdowns
  // =========================================================================

  initDropdowns() {
    const toggleButtons = this.container.querySelectorAll('[data-filter-toggle]');

    toggleButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        const wrapper = button.closest('[data-filter-wrapper]');
        const dropdown = wrapper?.querySelector('[data-filter-dropdown]');

        this.closeAllDropdowns(dropdown);

        if (dropdown) {
          const isOpen = dropdown.classList.contains('is-open');
          dropdown.classList.toggle('is-open', !isOpen);
          button.setAttribute('aria-expanded', !isOpen);
        }
      });
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-filter-wrapper]')) {
        this.closeAllDropdowns();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllDropdowns();
      }
    });
  }

  closeAllDropdowns(except = null) {
    const dropdowns = this.container.querySelectorAll('[data-filter-dropdown]');
    dropdowns.forEach(dropdown => {
      if (dropdown !== except) {
        dropdown.classList.remove('is-open');
        const button = dropdown.closest('[data-filter-wrapper]')?.querySelector('[data-filter-toggle]');
        if (button) {
          button.setAttribute('aria-expanded', 'false');
        }
      }
    });
  }

  // =========================================================================
  // Filter Events - Native URL Navigation
  // =========================================================================

  bindFilterEvents() {
    if (!this.filterForm) return;

    // Checkbox filter inputs - submit form on change
    const filterInputs = this.filterForm.querySelectorAll('[data-filter-input]');
    filterInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.submitFilterForm();
      });
    });

    // Sort inputs - submit form on change
    const sortInputs = this.filterForm.querySelectorAll('[data-sort-input]');
    sortInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.submitFilterForm();
      });
    });

    // Price filter apply button
    const priceApplyBtn = this.filterForm.querySelector('[data-price-apply]');
    if (priceApplyBtn) {
      priceApplyBtn.addEventListener('click', () => {
        this.submitFilterForm();
      });
    }

    // Price inputs - apply on Enter
    const priceInputs = this.filterForm.querySelectorAll('[data-price-input]');
    priceInputs.forEach(input => {
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.submitFilterForm();
        }
      });
    });
  }

  submitFilterForm() {
    if (!this.filterForm) return;

    // Remove empty price inputs so they don't clutter the URL
    const priceInputs = this.filterForm.querySelectorAll('[data-price-input]');
    priceInputs.forEach(input => {
      if (!input.value) {
        input.removeAttribute('name');
      }
    });

    // Remove unchecked checkboxes' name to keep URL clean (they won't submit anyway, but just in case)
    this.filterForm.submit();
  }

  updateActiveFiltersDisplay() {
    const clearBtn = this.container.querySelector('[data-filter-clear]');
    if (!clearBtn) return;

    const params = new URLSearchParams(window.location.search);
    let hasActiveFilters = false;

    for (const [key, value] of params.entries()) {
      if (key !== 'sort_by' && key !== 'page' && value) {
        hasActiveFilters = true;
        break;
      }
    }

    clearBtn.style.display = hasActiveFilters ? 'block' : 'none';
  }

  // =========================================================================
  // Pagination Events - Native URL Navigation
  // =========================================================================

  bindPaginationEvents() {
    this.container.addEventListener('click', (e) => {
      const link = e.target.closest('[data-page-link]');
      if (link) {
        // Allow normal navigation - no preventDefault needed
        return;
      }
    });
  }

  // =========================================================================
  // Add to Cart
  // =========================================================================

  bindAddToCartEvents() {
    this.container.addEventListener('click', (e) => {
      const button = e.target.closest('.collection-product-add-to-cart');
      if (!button || button.tagName === 'A') return;

      e.preventDefault();
      const variantId = button.dataset.variantId;
      if (variantId) {
        this.addToCart(variantId, button);
      }
    });
  }

  async addToCart(variantId, button) {
    if (button.classList.contains('loading')) return;

    button.classList.add('loading');
    const buttonText = button.querySelector('.button-text');
    const originalText = buttonText?.textContent || '';
    if (buttonText) buttonText.textContent = 'Dodawanie...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });

      if (!response.ok) throw new Error('Add to cart failed');

      this.showToast();
      this.updateCartCount();

    } catch (error) {
      console.error('Error adding to cart:', error);
      if (buttonText) buttonText.textContent = 'Błąd';
    } finally {
      setTimeout(() => {
        button.classList.remove('loading');
        if (buttonText) buttonText.textContent = originalText;
      }, 1000);
    }
  }

  showToast() {
    if (!this.toast) return;
    this.toast.classList.add('show');
    setTimeout(() => this.toast.classList.remove('show'), 3000);
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
      });

      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { item_count: cart.item_count }
      }));
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  // =========================================================================
  // Category Slider
  // =========================================================================

  initCategorySlider() {
    const swiperElement = this.container.querySelector(`.collection-category-swiper-${this.sectionId}`);

    if (swiperElement && typeof Swiper !== 'undefined') {
      new Swiper(`.collection-category-swiper-${this.sectionId}`, {
        slidesPerView: 1.2,
        spaceBetween: 20,
        speed: 600,
        breakpoints: {
          640: { slidesPerView: 2, spaceBetween: 24 },
          1024: { slidesPerView: 3, spaceBetween: 30 },
        },
      });
    }
  }

  // =========================================================================
  // SEO Content Toggle
  // =========================================================================

  initSeoToggle() {
    const toggleBtn = this.container.querySelector('[data-seo-toggle]');
    const wrapper = this.container.querySelector('[data-seo-wrapper]');
    const toggleText = this.container.querySelector('[data-seo-toggle-text]');

    if (!toggleBtn || !wrapper) return;

    // Check if content needs toggle (is taller than max-height)
    const content = wrapper.querySelector('[data-seo-content]');
    if (content && content.scrollHeight <= 200) {
      // Content fits, hide toggle wrapper and gradient
      const toggleWrapper = toggleBtn.closest('.collection-seo-toggle-wrapper');
      if (toggleWrapper) toggleWrapper.style.display = 'none';
      const gradient = wrapper.querySelector('[data-seo-gradient]');
      if (gradient) gradient.style.display = 'none';
      wrapper.style.maxHeight = 'none';
      return;
    }

    toggleBtn.addEventListener('click', () => {
      const isExpanded = wrapper.classList.contains('is-expanded');

      wrapper.classList.toggle('is-expanded', !isExpanded);
      toggleBtn.classList.toggle('is-expanded', !isExpanded);

      if (toggleText) {
        toggleText.textContent = isExpanded ? 'Przeczytaj więcej' : 'Zwiń';
      }
    });
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.collection-grid-section').forEach(container => {
    new CollectionGrid(container);
  });
});

// Reinitialize on section reload (Shopify editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    const container = e.target.querySelector('.collection-grid-section');
    if (container) new CollectionGrid(container);
  });
}
